import React, { useState } from "react";
import { Link } from "react-router-dom";

// ── Dummy data ───────────────────────────────────────────────
// The Admin Dashboard currently only *displays* data. Real create/edit/
// delete persistence against the backend is a planned next step, so the
// data below is hard-coded for now and the action buttons are
// intentionally no-ops with a short status message instead of a real
// mutation.
const DUMMY_POSTS = [
  { id: 1, title: "Building Decentralized Applications on Ethereum", status: "published", date: "2026-05-28", views: 482 },
  { id: 2, title: "Full Stack Development with the MERN Stack", status: "published", date: "2026-05-15", views: 731 },
  { id: 3, title: "Containerizing Node.js Apps with Docker & Kubernetes", status: "published", date: "2026-05-20", views: 356 },
  { id: 4, title: "Autonomous Robot Navigation with ROS and OpenCV", status: "draft", date: "2026-05-03", views: 0 },
  { id: 5, title: "Integrating OpenAI's API into Your Web Application", status: "published", date: "2026-04-21", views: 612 },
  { id: 6, title: "CI/CD Pipelines with GitHub Actions: Zero to Hero", status: "draft", date: "2026-04-10", views: 0 },
];

const DUMMY_USERS = [
  { id: 1, name: "Kambham Srihari Reddy", email: "srihari95027@gmail.com", role: "Admin", status: "active" },
  { id: 2, name: "Aliya Khan", email: "aliya.khan@example.com", role: "Editor", status: "active" },
  { id: 3, name: "Marcus Webb", email: "marcus.webb@example.com", role: "Subscriber", status: "active" },
  { id: 4, name: "Priya Nair", email: "priya.nair@example.com", role: "Subscriber", status: "inactive" },
];

const SIDEBAR_ITEMS = [
  { key: "overview", label: "Overview", icon: "📊" },
  { key: "blogs", label: "Blog Posts", icon: "📝", badge: DUMMY_POSTS.length },
  { key: "users", label: "Users", icon: "👥", badge: DUMMY_USERS.length },
];

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [postModalOpen, setPostModalOpen] = useState(false);
  const [notice, setNotice] = useState(null);

  const totalViews = DUMMY_POSTS.reduce((sum, post) => sum + post.views, 0);
  const publishedCount = DUMMY_POSTS.filter((p) => p.status === "published").length;
  const activeUserCount = DUMMY_USERS.filter((u) => u.status === "active").length;

  const flashNotice = (message) => {
    setNotice(message);
    window.clearTimeout(window.__adminNoticeTimeout);
    window.__adminNoticeTimeout = window.setTimeout(() => setNotice(null), 2500);
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div style={{ display: "flex" }}>
      {/* ─────────────── SIDEBAR ─────────────── */}
      <div
        className={`sidebar-overlay${sidebarOpen ? " open" : ""}`}
        onClick={closeSidebar}
      />
      <aside className={`sidebar${sidebarOpen ? " open" : ""}`}>
        <div className="sidebar-header">
          <Link to="/" style={{ color: "var(--text-primary)", fontWeight: 600, fontSize: "1.0625rem" }}>
            Srihari Reddy
          </Link>
          <p style={{ color: "var(--text-dim)", fontSize: "0.75rem", marginTop: "0.25rem" }}>
            Admin Dashboard
          </p>
        </div>

        <nav className="sidebar-nav">
          <p className="sidebar-section-label">Manage</p>
          {SIDEBAR_ITEMS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`sidebar-item${activeTab === item.key ? " active" : ""}`}
              style={{ width: "100%", textAlign: "left" }}
              onClick={() => {
                setActiveTab(item.key);
                closeSidebar();
              }}
            >
              <span>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.badge !== undefined && (
                <span className="badge badge-cyan">{item.badge}</span>
              )}
            </button>
          ))}

          <p className="sidebar-section-label">Site</p>
          <Link to="/" className="sidebar-item">
            <span>🏠</span> View Portfolio
          </Link>
          <Link to="/blogs" className="sidebar-item">
            <span>📰</span> View Blog
          </Link>
        </nav>
      </aside>

      {/* ─────────────── MAIN ─────────────── */}
      <div className="admin-main">
        <header className="admin-topbar">
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <button
              type="button"
              className="navbar-toggle admin-topbar-toggle"
              style={{ display: "none" }}
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
            >
              <svg style={{ width: 22, height: 22 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div>
              <h1 style={{ color: "var(--text-primary)", fontSize: "1.25rem" }}>
                {SIDEBAR_ITEMS.find((i) => i.key === activeTab)?.label || "Overview"}
              </h1>
              <p style={{ color: "var(--text-dim)", fontSize: "0.8125rem" }}>
                Sample data shown for now — CRUD wiring coming soon.
              </p>
            </div>
          </div>

          <button type="button" className="btn btn-primary btn-sm" onClick={() => setPostModalOpen(true)}>
            + New Post
          </button>
        </header>

        <div className="admin-content">
          {notice && (
            <div className="badge badge-cyan" style={{ marginBottom: "1.25rem", display: "inline-flex" }}>
              {notice}
            </div>
          )}

          {/* Tabs (visible on all sizes as a secondary way to switch) */}
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
            {SIDEBAR_ITEMS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`tab-btn${activeTab === item.key ? " active" : ""}`}
                onClick={() => setActiveTab(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* ─────────────── OVERVIEW ─────────────── */}
          {activeTab === "overview" && (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "1.25rem",
                  marginBottom: "2rem",
                }}
              >
                <div className="kpi-card">
                  <div className="kpi-value" style={{ color: "var(--accent)" }}>{DUMMY_POSTS.length}</div>
                  <div className="kpi-label">Total Posts</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value" style={{ color: "var(--emerald)" }}>{publishedCount}</div>
                  <div className="kpi-label">Published</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value" style={{ color: "var(--purple)" }}>{totalViews.toLocaleString()}</div>
                  <div className="kpi-label">Total Views</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-value" style={{ color: "var(--amber)" }}>{activeUserCount}</div>
                  <div className="kpi-label">Active Users</div>
                </div>
              </div>

              <h2 style={{ color: "var(--text-primary)", fontSize: "1.125rem", marginBottom: "1rem" }}>
                Recent Posts
              </h2>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Views</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DUMMY_POSTS.slice(0, 4).map((post) => (
                      <tr key={post.id}>
                        <td style={{ color: "var(--text-primary)" }}>{post.title}</td>
                        <td>
                          <span className={`status-dot status-${post.status}`} />
                          {post.status}
                        </td>
                        <td>{post.date}</td>
                        <td>{post.views.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ─────────────── BLOG POSTS ─────────────── */}
          {activeTab === "blogs" && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Views</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {DUMMY_POSTS.map((post) => (
                    <tr key={post.id}>
                      <td style={{ color: "var(--text-primary)" }}>{post.title}</td>
                      <td>
                        <span className={`status-dot status-${post.status}`} />
                        {post.status}
                      </td>
                      <td>{post.date}</td>
                      <td>{post.views.toLocaleString()}</td>
                      <td>
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <button
                            type="button"
                            className="action-btn edit"
                            title="Edit"
                            onClick={() => flashNotice("Editing posts is coming soon.")}
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="action-btn delete"
                            title="Delete"
                            onClick={() => flashNotice("Deleting posts is coming soon.")}
                          >
                            🗑
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ─────────────── USERS ─────────────── */}
          {activeTab === "users" && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {DUMMY_USERS.map((user) => (
                    <tr key={user.id}>
                      <td style={{ color: "var(--text-primary)" }}>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.role}</td>
                      <td>
                        <span className={`status-dot status-${user.status}`} />
                        {user.status}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <button
                            type="button"
                            className="action-btn edit"
                            title="Edit"
                            onClick={() => flashNotice("Editing users is coming soon.")}
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="action-btn delete"
                            title="Delete"
                            disabled={user.role === "Admin"}
                            onClick={() => flashNotice("Deleting users is coming soon.")}
                          >
                            🗑
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ─────────────── NEW POST MODAL ─────────────── */}
      {postModalOpen && (
        <div className="modal-overlay" onClick={() => setPostModalOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: "var(--text-primary)" }}>New Blog Post</h3>
              <button
                type="button"
                className="btn-ghost btn btn-sm"
                onClick={() => setPostModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Title</label>
                <input type="text" className="form-input" placeholder="Post title" disabled />
              </div>
              <div className="form-group">
                <label className="form-label">Summary</label>
                <textarea className="form-textarea" placeholder="Short summary" disabled />
              </div>
              <p style={{ color: "var(--text-dim)", fontSize: "0.8125rem" }}>
                Form fields are disabled — creating real posts is coming soon.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setPostModalOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setPostModalOpen(false);
                  flashNotice("Creating posts is coming soon.");
                }}
              >
                Save Post
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
